import { Component, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { safeErrorLog } from '@drive-without-fear/core/security/gdpr'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, _errorInfo: React.ErrorInfo) {
    // Log error to console in development
    safeErrorLog('ErrorBoundary caught an error:', error)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-[#FBF9F5] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            {/* Error Header */}
            <div className="bg-gradient-to-br from-[#C7796B] to-[#B56A5D] p-8 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Something Went Wrong</h1>
              <p className="text-white/80">
                We're sorry, but something unexpected happened
              </p>
            </div>

            {/* Error Content */}
            <div className="p-6">
              <p className="text-[#6E7582] mb-6 text-center">
                Don't worry - your data is safe. You can try refreshing the page or
                return to the home page.
              </p>

              {/* Error Details (development only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-[#C7796B]/8 rounded-lg p-4 mb-6">
                  <p className="text-xs font-mono text-[#C7796B] break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={this.handleRetry}
                  className="w-full py-3 bg-[#D4AF5A] text-white font-semibold rounded-xl hover:bg-[#C09A4E] transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Try Again
                </button>
                <Link
                  to="/u/dashboard"
                  className="w-full py-3 border border-[#E3DED2] text-[#0F1C2E] font-semibold rounded-xl hover:bg-[#FBF9F5] transition-colors flex items-center justify-center gap-2"
                >
                  <Home className="w-5 h-5" />
                  Go to Dashboard
                </Link>
              </div>

              {/* Support Note */}
              <p className="text-center text-xs text-[#A4A9B3] mt-6">
                If this problem persists, please contact{' '}
                <a href="mailto:support@drivewithoutfear.com" className="text-[#4A6484] hover:underline">
                  support@drivewithoutfear.com
                </a>
              </p>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
